import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Search, Award, Download, Calendar, User, Briefcase, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../components/ui/Button';
import API_URL from '../config';

const VerifyCertificate = () => {
  const { credential_id } = useParams();
  const navigate = useNavigate();
  
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (credential_id) {
      setSearchInput(credential_id);
      verifyCertificate(credential_id);
    }
  }, [credential_id]);

  const verifyCertificate = async (idToVerify) => {
    if (!idToVerify) return;
    
    setLoading(true);
    setError('');
    setCertificateData(null);
    
    try {
      const response = await fetch(`${API_URL}/api/certificates/${idToVerify}/verify`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Certificate record not found. Please verify the Credential ID.');
      }
      
      const data = await response.json();
      setCertificateData(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while verifying the credential.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/verify/${searchInput.trim()}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950 pt-28 pb-20 px-4">
      <div className="max-w-3xl mx-auto">
        
        {/* Navigation Back */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-brand-600 transition-colors mb-8 group font-medium text-sm"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span>Back to Homepage</span>
        </button>

        {/* Header */}
        <div className="text-center mb-10">
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center justify-center p-3.5 bg-brand-50 dark:bg-brand-950/60 border border-brand-200/60 rounded-2xl mb-5 text-brand-600"
          >
            <Award className="w-8 h-8" />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-3 tracking-tight"
          >
            Certificate Verification Portal
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-slate-600 dark:text-slate-300 text-base max-w-xl mx-auto"
          >
            Verify the authenticity and credentials of certificates issued by Mira Future Tech Vision Pvt. Ltd.
          </motion.p>
        </div>

        {/* Search Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 md:p-6 shadow-subtle mb-8"
        >
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Enter Credential ID or Certificate Number (e.g. MFTV-INT-...)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl pl-11 pr-4 py-3 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all font-mono"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              className="py-3 px-6 font-semibold shadow-xs whitespace-nowrap"
            >
              {loading ? 'Verifying...' : 'Verify Credential'}
            </Button>
          </form>
        </motion.div>

        {/* Verification Result Feedback */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 p-7 rounded-2xl text-center"
            >
              <XCircle className="w-12 h-12 text-rose-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">Verification Unsuccessful</h3>
              <p className="text-rose-700 dark:text-rose-300 text-sm">{error}</p>
            </motion.div>
          )}

          {certificateData && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className={`bg-white dark:bg-slate-900 border rounded-3xl p-8 shadow-card ${
                certificateData.status === 'VALID' 
                  ? 'border-emerald-300 dark:border-emerald-800' 
                  : 'border-rose-300 dark:border-rose-800'
              }`}
            >
              {/* Status Header */}
              <div className="text-center pb-8 border-b border-slate-100 dark:border-slate-800 mb-8">
                {certificateData.status === 'VALID' ? (
                  <>
                    <div className="inline-flex items-center justify-center p-3 bg-emerald-50 dark:bg-emerald-950/60 rounded-full mb-4">
                      <CheckCircle className="w-10 h-10 text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-display font-bold text-emerald-700 dark:text-emerald-400 mb-1">
                      AUTHENTIC CERTIFICATE VERIFIED
                    </h2>
                    <p className="text-xs text-slate-500">
                      This certificate has been cryptographically verified against the official registry of Mira Future Tech Vision Pvt. Ltd.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center p-3 bg-rose-50 dark:bg-rose-950/60 rounded-full mb-4">
                      <XCircle className="w-10 h-10 text-rose-600" />
                    </div>
                    <h2 className="text-2xl font-display font-bold text-rose-700 dark:text-rose-400 mb-1">
                      CERTIFICATE INACTIVE OR REVOKED
                    </h2>
                    <p className="text-xs text-slate-500">
                      This certificate status is revoked or no longer active in our database.
                    </p>
                  </>
                )}
              </div>

              {/* Data Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 text-sm">
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Recipient Name
                  </span>
                  <p className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <User size={16} className="text-brand-600" />
                    {certificateData.recipient_name}
                  </p>
                </div>
                
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Domain / Track
                  </span>
                  <p className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Briefcase size={16} className="text-indigoBrand-600" />
                    {certificateData.internship_domain}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Program Duration
                  </span>
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Calendar size={15} className="text-slate-400" />
                    {new Date(certificateData.start_date).toLocaleDateString()} – {new Date(certificateData.end_date).toLocaleDateString()}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                    Credential Type
                  </span>
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Award size={15} className="text-amber-600" />
                    {certificateData.certificate_type || 'Internship Completion'}
                  </p>
                </div>
              </div>

              {/* Cryptographic Registry IDs */}
              <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                      Credential ID
                    </span>
                    <span className="font-mono text-brand-700 dark:text-brand-300 font-bold">
                      {certificateData.credential_id}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                      Certificate Serial
                    </span>
                    <span className="font-mono text-brand-700 dark:text-brand-300 font-bold">
                      {certificateData.certificate_number}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                      Issuance Date
                    </span>
                    <span className="text-slate-700 dark:text-slate-300">
                      {new Date(certificateData.issue_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                      Issuing Entity
                    </span>
                    <span className="text-slate-700 dark:text-slate-300">
                      Mira Future Tech Vision Pvt. Ltd.
                    </span>
                  </div>
                </div>
              </div>

              {/* Download Option if present */}
              {certificateData.status === 'VALID' && certificateData.pdf_path && (
                <div className="text-center pt-2">
                  <a
                    href={`${API_URL}/${certificateData.pdf_path}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="primary" className="flex items-center gap-2 mx-auto">
                      <Download size={16} /> Download Official PDF Certificate
                    </Button>
                  </a>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default VerifyCertificate;
